import type { executeFetch as ExecuteFetchType } from '../fetch';
import type { FetchOptions } from '../types';

/**
 * POST to the logout endpoint and redirect to the URL the server lands on.
 * Falls back to fallbackUrl (should be a safe GET-navigable URL e.g. '/') on
 * network failure or if the server does not issue a redirect.
 *
 * Uses executeFetch (passed in to avoid circular imports) with returnRawResponse:true
 * so we receive the raw Response and can inspect res.redirected / res.url.
 * ky auto-follows 3xx redirects, so the final destination is in res.url — the
 * Location header is not visible on the followed response.
 */
export const postLogoutAndRedirect = async ({
  logoutUrl,
  fallbackUrl,
  options,
  executeFetch,
}: {
  logoutUrl: string;
  fallbackUrl: string;
  options?: FetchOptions;
  executeFetch: typeof ExecuteFetchType;
}): Promise<void> => {
  try {
    const res = await executeFetch<Response>(
      { url: logoutUrl, method: 'POST' },
      { ...options, returnRawResponse: true, shouldRedirect: false },
    );
    // ky follows 3xx automatically; res.redirected + res.url give the final destination
    const redirectUrl = res.redirected ? res.url : null;
    window.location.href = redirectUrl ?? fallbackUrl;
  } catch {
    // network failure — fallback to the current page (window.location.href passed by caller)
    window.location.href = fallbackUrl;
  }
};
